/**
 * Skimmer Sync Invoice Failure Exception Config
 *
 * Covers: ex-sale-sk-ls-invoice-sync-failure
 *
 * Invoices the external Skimmer to Lightspeed sync tool could not
 * reconcile. Backend reads straight from franchise_exception_tags +
 * exception_metadata (no ls_sales join) because the tagged object_id is
 * a Skimmer invoice number, not a Lightspeed ls_id. See
 * docs/plans/pending/feature-skimmer-sync-error-ingest.md.
 */
import React from 'react'
import {
  Box,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { typographyEl } from '../shared/muiElements'
import {
  OpenInNew as OpenInNewIcon,
  WarningAmber as WarningAmberIcon,
  Inventory2 as Inventory2Icon,
  AltRoute as AltRouteIcon,
  Build as BuildIcon,
  Handyman as HandymanIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { textPart } from '../shared/rowSubtitle'
import {
  renderDaysOpen,
  renderLastReportedLink,
} from '../shared/skimmerSyncRenderers'
import type {
  ExceptionConfig,
  ExceptionColumnDef,
  ExceptionFilterStripDef,
} from '../types'
import {
  formatBusinessDate,
  daysOpenColor,
} from '../../../utils/inspectorFormatters'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'

interface Issue {
  category?: string
  error_code?: string
  reason?: string | null
  detail?: string | null
  // Short chip-sized form ("Product line error"). Preferred for chips +
  // Excel summary; falls back to title.
  label?: string | null
  // Full descriptive form ("Product not matched in Lightspeed"). Used
  // for the issue card header and any place with room for the long form.
  title?: string | null
  owner?: 'fp' | 'psc' | null
  summary?: string | null
}

const getIssues = (row: Record<string, any>): Issue[] =>
  Array.isArray(row.issues) ? row.issues : []

const renderInvoiceLink = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const invoice = row.invoice_number || row.object_id || '-'
  if (!row.skimmer_url) {
    return typographyEl(
      { component: 'span', sx: { fontSize: '1rem', fontWeight: 500 } },
      invoice,
    )
  }
  return React.createElement(
    Link,
    {
      href: row.skimmer_url,
      target: '_blank',
      rel: 'noopener noreferrer',
      sx: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontWeight: 500,
        fontSize: '1rem',
      },
    },
    invoice,
    React.createElement(OpenInNewIcon, { fontSize: 'inherit' }),
  )
}

const groupIssues = (
  issues: Issue[],
): Array<{ code: string; label: string; title: string; owner: string; count: number; details: string[]; helper: Issue }> => {
  const map = new Map<string, { code: string; label: string; title: string; owner: string; count: number; details: string[]; helper: Issue }>()
  for (const issue of issues) {
    const code = issue.error_code || issue.category || 'unknown'
    const title = issue.title || issue.reason || 'Sync failure'
    // Short chip label - falls back to title when the catalog hasn't
    // defined one yet (legacy tags or new codes added before the JSON
    // edit lands).
    const label = issue.label || title
    const owner = (issue.owner ?? 'psc') as string
    const existing = map.get(code)
    if (existing) {
      existing.count += 1
      if (issue.detail && !existing.details.includes(issue.detail)) {
        existing.details.push(issue.detail)
      }
      continue
    }
    map.set(code, {
      code,
      label,
      title,
      owner,
      count: 1,
      details: issue.detail ? [issue.detail] : [],
      helper: issue,
    })
  }
  // Map.forEach via for...of preserves insertion order — keep it stable so
  // FP-owned chips don't jump around between renders.
  return Array.from(map.values()).sort((a, b) => {
    if (a.owner !== b.owner) return a.owner === 'fp' ? -1 : 1
    return b.count - a.count
  })
}

const renderIssuesAggregated = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const issues = getIssues(row)
  if (issues.length === 0) return '-'
  const grouped = groupIssues(issues)
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {grouped.map((g, idx) => {
        const chipLabel = g.count > 1 ? `${g.label} ×${g.count}` : g.label
        // Tooltip body: the affected line items when present, else the
        // long-form failure title so a hover always says more than the chip.
        const tooltipTitle =
          g.details.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {g.details.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </Box>
          ) : (
            g.title
          )
        // Tone signals owner: FP-actionable failures read as "warning"
        // (orange outline), PSC-handled failures read as neutral grey.
        return (
          <Tooltip
            key={`${g.code}-${idx}`}
            title={tooltipTitle}
            arrow
            placement="top"
          >
            {/* span wrapper lets the Tooltip attach without intercepting the
             * row-open click (precedent: IssuesChip.tsx). */}
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Chip
                {...statusPillChipProps(
                  chipLabel,
                  g.owner === 'fp' ? 'warning' : 'neutral',
                )}
              />
            </Box>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

// Unified Status column. Drives both the Active and Fixed views: when a
// row has `resolved_at` set we render the fix date with a positive
// tone, otherwise we render the live "Stuck X days" indicator using the
// same shared `renderDaysOpen` formatter the older Days Open column
// used. Lets one config (and one card/search row) serve both tabs.
const renderStatus = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const resolved = row.resolved_at
  if (resolved) {
    return React.createElement(
      Stack,
      {
        direction: 'row',
        spacing: 0.5,
        alignItems: 'center',
        justifyContent: 'center',
      },
      typographyEl(
        {
          component: 'span',
          variant: 'body2',
          sx: { color: '#2E7D32', fontWeight: 600 },
        },
        '✓ Fixed',
      ),
      typographyEl(
        {
          component: 'span',
          variant: 'body2',
          sx: { color: 'text.secondary' },
        },
        formatBusinessDate(resolved),
      ),
    )
  }
  return renderDaysOpen(row.days_open, row)
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'invoice_number',
    label: 'Invoice',
    contexts: ['inspector'],
    render: renderInvoiceLink,
    getSortValue: (row) => row.invoice_number || row.object_id || '',
  },
  {
    key: 'issues',
    label: 'Failures',
    contexts: ['inspector'],
    render: renderIssuesAggregated,
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    contexts: ['inspector'],
    render: renderStatus,
    // Sort key collapses both states onto one axis: resolved rows sort
    // by resolution recency (most-recently-fixed first); active rows
    // sort by days open (oldest stuck first). The sign offset keeps
    // the two regions monotonic so a desc sort reads as "what needs
    // attention first" at the top in either mode.
    getSortValue: (row) => {
      if (row.resolved_at) {
        const ts = Date.parse(row.resolved_at)
        return Number.isFinite(ts) ? ts : 0
      }
      const days = typeof row.days_open === 'number' ? row.days_open : 0
      // Boost active rows above any plausible resolved timestamp so a
      // mixed page (shouldn't happen in practice, but guard anyway)
      // surfaces the still-stuck rows first in desc order.
      return 1e15 + days
    },
  },
  {
    key: 'last_reported_at',
    label: 'Last Reported',
    align: 'center',
    contexts: ['inspector'],
    render: renderLastReportedLink,
    getSortValue: (row) => row.last_reported_at || '',
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'invoice_number',
    label: 'Invoice',
    contexts: ['platform'],
    render: renderInvoiceLink,
  },
  {
    key: 'issues',
    label: 'Failures',
    contexts: ['platform'],
    render: renderIssuesAggregated,
  },
  {
    key: 'failure_details',
    label: 'Failure Details',
    align: 'left',
    contexts: ['platform'],
    render: (_value, row) => {
      const issues: any[] = Array.isArray(row.issues) ? row.issues : []
      const details = issues.flatMap((i) => (i?.detail ? [String(i.detail)] : []))
      return details.length ? details.join(', ') : '-'
    },
  },
  {
    key: 'days_open',
    label: 'Days Open',
    align: 'center',
    contexts: ['platform'],
    render: renderDaysOpen,
    getSortValue: (row) =>
      typeof row.days_open === 'number' ? row.days_open : -1,
  },
  {
    key: 'last_reported_at',
    label: 'Last Reported',
    align: 'center',
    contexts: ['platform'],
    render: renderLastReportedLink,
    getSortValue: (row) => row.last_reported_at || '',
    exportFormat: 'date',
  },
]

// ── SK-LS page recipe ────────────────────────────────────────────────
//
// SK-LS uses one config for both Active and Fixed views. The Status
// column renders contextually (presence of `resolved_at` decides which
// chip style); everything else - cards, search, columns - is shared.
const SK_SYNC_FIXED_WINDOW_DAYS = 30
const SK_SYNC_DEFAULT_PAGE_SIZE = 50
const SK_SYNC_PAGE_SIZE_OPTIONS = [25, 50, 100, 250]

// Synthetic key for the PSC-handles rollup card. Translates to
// `?owner=psc` in the URL; mutually exclusive with an `?issue=`.
const PSC_KEY = '__psc__'

// Active card lights up when activeKey matches; otherwise see selection
// adapter below.
const skSyncIssueStrip: ExceptionFilterStripDef = {
  type: 'cards',
  buildCards: (response) => {
    const counts = (response?.counts ?? null) as Record<string, number> | null
    const get = (key: string): number =>
      counts ? Number(counts[key] ?? 0) : 0
    return [
      { key: null, label: 'All stuck', count: get('all'), icon: <WarningAmberIcon /> },
      {
        key: 'line_item_product_mapping_missing',
        label: 'Product',
        count: get('line_item_product_mapping_missing'),
        icon: <Inventory2Icon />,
      },
      {
        key: 'line_item_route_stop_mapping_missing',
        label: 'Route stop',
        count: get('line_item_route_stop_mapping_missing'),
        icon: <AltRouteIcon />,
      },
      {
        key: 'line_item_work_order_mapping_missing',
        label: 'Work order',
        count: get('line_item_work_order_mapping_missing'),
        icon: <BuildIcon />,
      },
      {
        key: 'line_item_installed_item_mapping_missing',
        label: 'Installed item',
        count: get('line_item_installed_item_mapping_missing'),
        icon: <HandymanIcon />,
      },
      {
        key: 'unmatched_customer',
        label: 'Client',
        count: get('unmatched_customer'),
        icon: <PersonIcon />,
      },
      { key: PSC_KEY, label: 'PSC handles', count: get('psc'), icon: <BusinessIcon /> },
    ]
  },
  // Compose the active card key from the URL's two axes (`issue`,
  // `owner`). Only one is set at a time - applySelection enforces
  // mutual exclusivity.
  getActiveKey: (params) => {
    if (params.get('owner') === 'psc') return PSC_KEY
    return params.get('issue')
  },
  // Toggles: re-clicking the active card clears the filter. Clearing
  // happens because the new params have neither `issue` nor `owner`
  // unless we explicitly re-set below.
  applySelection: (params, key) => {
    const next = new URLSearchParams(params)
    next.delete('issue')
    next.delete('owner')
    if (key === PSC_KEY) {
      if (params.get('owner') !== 'psc') next.set('owner', 'psc')
    } else if (key) {
      if (params.get('issue') !== key) next.set('issue', key)
    }
    return next
  },
}

// Panel footer metadata: oldest-open + latest-report. Lives on the config
// itself (not on a strip) because in the unified DataViewFilterPanel
// layout, metadata is rendered in the footer next to tabs and the search
// input - it's panel-level context, not card-strip-level.
const skSyncPanelMetadata = (response: Record<string, any> | null): React.ReactNode => {
  const oldestDaysOpen =
    typeof response?.oldest_days_open === 'number'
      ? (response.oldest_days_open as number)
      : null
  const latestReportAt =
    (response?.latest_report_at as string | null | undefined) ?? null
  if (oldestDaysOpen == null && !latestReportAt) return null
  const oldestColorToken = daysOpenColor(oldestDaysOpen)
  const oldestColor =
    oldestColorToken === 'error.main'
      ? '#C62828'
      : oldestColorToken === 'warning.main'
        ? '#EF6C00'
        : 'text.secondary'
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ flexWrap: 'wrap', color: 'text.secondary' }}
    >
      {oldestDaysOpen != null && (
        <Box>
          <Typography
            component="span"
            variant="caption"
            sx={{ color: 'text.secondary', mr: 0.5 }}
          >
            Oldest open:
          </Typography>
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: 600, color: oldestColor }}
          >
            {`${oldestDaysOpen} day${oldestDaysOpen === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      )}
      {latestReportAt && (
        <Box>
          <Typography
            component="span"
            variant="caption"
            sx={{ color: 'text.secondary', mr: 0.5 }}
          >
            Latest report:
          </Typography>
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            {formatBusinessDate(latestReportAt)}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

export const skimmerSyncInvoiceConfig: ExceptionConfig = {
  codes: ['ex-sale-sk-ls-invoice-sync-failure'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'status',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'object_id',
  getRowTitle: row =>
    textPart(row.invoice_number) || textPart(row.object_id) || 'Invoice',
  // Server-driven search input rendered in the table toolbar by
  // DataViewTable. The host route owns the URL-synced state and
  // passes it via the `serverSearch` prop.
  filters: [
    {
      key: 'search',
      type: 'search',
      label: 'Search',
      placeholder: 'Search invoice # or detail',
    },
  ],
  serverTable: {
    defaultPageSize: SK_SYNC_DEFAULT_PAGE_SIZE,
    pageSizeOptions: SK_SYNC_PAGE_SIZE_OPTIONS,
    enableSearch: true,
  },
  // Status (Open / Closed) sits in the page header pill -
  // see ExceptionStatusToggle wired through DynamicExceptionRoute.
  // Only the issue cards live in the filter panel.
  filterStrips: [skSyncIssueStrip],
  panelMetadata: skSyncPanelMetadata,
  buildFetchParams: (urlState) => {
    const params: Record<string, any> = {}
    // Baseline (status / paging / search) is applied by
    // ``useExceptionDataFetch``; we only add the secondary
    // ``since_days`` window when the lifecycle slice is closed.
    const status = urlState.searchParams.get('status')
    if (status === 'closed' || status === 'fixed' || status === 'resolved') {
      params.since_days = SK_SYNC_FIXED_WINDOW_DAYS
    }
    const issue = urlState.searchParams.get('issue')
    if (issue) params.issue_code = issue
    if (urlState.searchParams.get('owner') === 'psc') params.owner = 'psc'
    return params
  },
}
